import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon, { IconComponent } from './Icon';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'link';
type Size = 'sm' | 'md';

interface BaseProps {
  variant?: Variant;
  size?: Size;
  leadingIcon?: IconComponent;
  trailingIcon?: IconComponent;
  fullWidth?: boolean;
  loading?: boolean;
  className?: string;
  children: React.ReactNode;
}

type ButtonAsButton = BaseProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & { href?: never };
type ButtonAsAnchor = BaseProps & { href: string; target?: string; onClick?: React.MouseEventHandler<HTMLAnchorElement> };

export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

const base =
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-font-semibold tw-whitespace-nowrap tw-transition-colors tw-duration-200 ' +
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-ds-canvas';

const variants: Record<Variant, string> = {
  primary: 'tw-bg-ds-brand tw-text-ds-on-brand hover:tw-brightness-95',
  secondary: 'tw-bg-ds-surface tw-text-ds-text-primary tw-border tw-border-ds-border-strong hover:tw-bg-ds-subtle',
  danger: 'tw-bg-ds-danger-bg tw-text-ds-danger-fg hover:tw-brightness-95',
  ghost: 'tw-text-ds-brand-text hover:tw-underline',
  link: 'tw-text-ds-brand-text hover:tw-underline',
};

// Filled/outlined buttons come in two sizes; ghost and link only appear at one size in the designs.
const boxSizes: Record<Size, string> = {
  md: 'tw-px-5 tw-py-[13px] tw-rounded-xl tw-text-[15px] tw-leading-5',
  sm: 'tw-px-[14px] tw-py-[9px] tw-rounded-[10px] tw-text-[13px] tw-leading-[18px]',
};
const sizeFor = (variant: Variant, size: Size) =>
  variant === 'ghost'
    ? 'tw-px-0.5 tw-py-2.5 tw-rounded-xl tw-text-[15px] tw-leading-5'
    : variant === 'link'
      ? 'tw-gap-1.5 tw-text-[14px] tw-leading-5'
      : boxSizes[size];

// Matches the design's disabled primary button ("Get OTP" before a valid number).
const disabledStyles = 'disabled:tw-cursor-not-allowed disabled:tw-bg-ds-subtle disabled:tw-text-ds-text-tertiary disabled:tw-border-transparent disabled:hover:tw-brightness-100';

/** Design-system button. Renders an `<a>` with `href`, otherwise a `<button>`. */
const Button: React.FC<ButtonProps> = (props) => {
  const {
    variant = 'primary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    fullWidth,
    loading,
    className,
    children,
    ...rest
  } = props;

  const iconSize = variant === 'link' ? 14 : size === 'sm' ? 16 : 18;
  const classes = cn(base, variants[variant], sizeFor(variant, size), disabledStyles, fullWidth && 'tw-w-full', className);

  const content = (
    <>
      {loading ? (
        <Loader2 className="tw-h-[18px] tw-w-[18px] tw-animate-spin" aria-hidden />
      ) : (
        leadingIcon && <Icon icon={leadingIcon} size={iconSize} />
      )}
      <span>{children}</span>
      {trailingIcon && !loading && <Icon icon={trailingIcon} size={iconSize} />}
    </>
  );

  if ('href' in rest && rest.href) {
    const { href, target, ...anchorRest } = rest as ButtonAsAnchor;
    return (
      <a href={href} target={target} rel={target === '_blank' ? 'noopener noreferrer' : undefined} className={classes} {...anchorRest}>
        {content}
      </a>
    );
  }

  const { type = 'button', disabled, ...buttonRest } = rest as React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type={type} disabled={disabled || loading} aria-busy={loading || undefined} className={classes} {...buttonRest}>
      {content}
    </button>
  );
};

export default Button;
